// Pure helpers for the payment link page (/external/payment/:lang/:orderId) and the
// EveryPay return page (/external/success-payment/:lang?subject=&id=[&m=bank]).
// No React, no window: everything here is unit-tested in paymentSubject.test.js.

export const SUBJECTS = Object.freeze(['order', 'completed', 'tip', 'package']);

// Airtable rec id: "rec" + 14 alphanumerics, 17 characters in total.
const REC_ID_RE = /^rec[A-Za-z0-9]{14}$/;
// Package.Nr is an Airtable autonumber: positive integer, no leading zero.
const PACKAGE_NR_RE = /^[1-9]\d{0,9}$/;

/** True for a Package.Nr (Airtable autonumber) as it appears in a URL. */
export function isPackageNr(value) {
    return typeof value === 'string' && PACKAGE_NR_RE.test(value.trim());
}

// Legacy links built by ws1 create_payment_link_new: "<recid>tips" and
// "<recid>completed" (wiseclean-web/CLAUDE.md, "suffix-convention"). Only the
// tips form was ever generated (Rating.jsx and the thanks_for_rating email), but
// both keep working for links already in inboxes.
const LEGACY_SUFFIXES = [
    ['tips', 'tip'],
    ['completed', 'completed'],
];

function readQuery(query, name) {
    if (!query) return '';
    if (typeof query.get === 'function') return String(query.get(name) || '').trim();
    if (typeof query === 'object' && typeof query[name] === 'string') return query[name].trim();
    return '';
}

/**
 * Resolve the route param + query string into a payment subject.
 *
 * Order of precedence (spec §4.8): a legacy suffix wins, then ?subject=, then
 * "order". A suffix is honoured ONLY when what is left in front of it is a full
 * 17-character rec id; a bare rec id that merely ends in "tips" is still an
 * order id. `defaulted` is true when neither a suffix nor a valid ?subject= was
 * present: the page then retries a not_found order once as `completed`, because
 * admin links for completed orders carry a bare rec id.
 *
 * @param {string} raw    the :orderId route param
 * @param {URLSearchParams|{subject?: string}|null} query
 * @returns {{subject: string, id: string, legacy: boolean, defaulted: boolean}|null} null = not a usable link
 */
export function parseOrderIdParam(raw, query) {
    const value = typeof raw === 'string' ? raw.trim() : '';
    if (!value) return null;

    for (const [suffix, subject] of LEGACY_SUFFIXES) {
        if (value.length > suffix.length && value.endsWith(suffix)) {
            const id = value.slice(0, -suffix.length);
            if (REC_ID_RE.test(id)) return { subject, id, legacy: true, defaulted: false };
        }
    }

    const requested = readQuery(query, 'subject').toLowerCase();
    const explicit = SUBJECTS.includes(requested);
    const subject = explicit ? requested : 'order';

    if (subject === 'package') {
        return (PACKAGE_NR_RE.test(value) || REC_ID_RE.test(value)) ? { subject, id: value, legacy: false, defaulted: false } : null;
    }
    return REC_ID_RE.test(value) ? { subject, id: value, legacy: false, defaulted: !explicit } : null;
}

/**
 * Parse the EveryPay return query built by ws5 pay/start (spec §4.2):
 * ?subject=<s>&id=<recid>[&m=bank]. The id is always a rec id here (pay/start
 * takes rec ids only), so a package return carries the Package rec id, not Nr.
 *
 * @returns {{subject: string, id: string, bank: boolean}|null}
 */
export function parseReturnQuery(query) {
    const subject = readQuery(query, 'subject').toLowerCase();
    const id = readQuery(query, 'id');
    if (!SUBJECTS.includes(subject) || !REC_ID_RE.test(id)) return null;
    return { subject, id, bank: readQuery(query, 'm') === 'bank' };
}

/**
 * The subject to pay with after pay/context. ws5 answers a subject=order link
 * whose cleaning was finished meanwhile with the archive record
 * (effective_subject "completed", recid = the Orders_completed rec id), because
 * the Orders row is deleted at finish. Only that order -> completed hand-off is
 * accepted; anything else, or an older ws5 without the field, keeps the
 * requested subject. The page must then send this subject with body.recid to
 * pay/start and skip the order-only contact pre-check.
 *
 * @param {string} requested the subject the page asked pay/context for
 * @param {{effective_subject?: string}|null} body the pay/context response
 * @returns {string}
 */
export function effectiveSubject(requested, body) {
    const served = String((body && body.effective_subject) || '').trim().toLowerCase();
    return requested === 'order' && served === 'completed' ? 'completed' : requested;
}

// ws5 refuse_reason -> i18n key for the refused screen (spec §4.1, §4.6, §5).
// Vocabulary (snake_case, shared by pay/context and pay/start):
//   not_available | bad_subject | unavailable | not_found | order_cancelled |
//   nothing_to_pay | already_paid | already_tipped | tip_pending_in_app |
//   tip_not_whole | tip_too_large | amount_changed | payment_in_review
// payment_in_review (2026-11-02): a finished order's link followed to its archive
// while an earlier payment for the same order is open or under review.
// `unavailable` is the transient one (Airtable 429/5xx): the page retries it
// twice before showing payMethodsUnavailable. `amount_changed` is handled by the
// page (re-run context) and never reaches this map. tip_not_whole, tip_too_large,
// bad_subject and anything unknown fall back to the generic support copy so a
// new server reason never renders a raw key.
const REFUSE_KEYS = Object.freeze({
    not_found: 'refuseNotFound',
    order_cancelled: 'refuseCancelled',
    nothing_to_pay: 'refuseNothingToPay',
    already_paid: 'refuseAlreadyPaid',
    already_tipped: 'refuseAlreadyTipped',
    tip_pending_in_app: 'refuseTipPendingInApp',
    not_available: 'refuseNotAvailable',
    payment_in_review: 'refusePaymentInReview',
    unavailable: 'payMethodsUnavailable',
});

const RETRYABLE_REFUSE = Object.freeze(['unavailable']);

function normaliseReason(reason) {
    return String(reason || '').trim().toLowerCase();
}

export function refuseReasonKey(reason) {
    const key = normaliseReason(reason);
    return Object.prototype.hasOwnProperty.call(REFUSE_KEYS, key) ? REFUSE_KEYS[key] : 'criticalErrorBody';
}

/** True for refuse reasons the page should retry (bounded) before giving up. */
export function isRetryableRefuse(reason) {
    return RETRYABLE_REFUSE.includes(normaliseReason(reason));
}

const SUBJECT_HEADING_KEYS = Object.freeze({
    order: 'paySubjectOrder',
    completed: 'paySubjectCompleted',
    tip: 'paySubjectTip',
    package: 'paySubjectPackage',
});

export function subjectHeadingKey(subject) {
    return SUBJECT_HEADING_KEYS[subject] || 'paySubjectOrder';
}

// Subject-aware body line on the success screen (spec §4.8).
const SUCCESS_BODY_KEYS = Object.freeze({
    order: 'successPaymentSuccessReceipt',
    completed: 'successPaymentCompletedBody',
    tip: 'successPaymentTipBody',
    package: 'successPaymentPackageLinkBody',
});

export function successBodyKey(subject) {
    return SUCCESS_BODY_KEYS[subject] || SUCCESS_BODY_KEYS.order;
}

// pay/verify polling plan (spec §4.3): 6 x 2 s, 8 x when the customer paid through
// a bank link (the bank redirects back before the callback more often).
export const VERIFY_POLL_INTERVAL_MS = 2000;

export function verifyPollAttempts(isBank) {
    return isBank === true ? 8 : 6;
}

/**
 * Map a pay/verify body onto the SuccessPayment screen state.
 *  - paid:true                  -> 'success'
 *  - status pending|processing  -> 'pending'   (keep polling)
 *  - status unreconcilable      -> 'checking'  (received, being checked; no retry button)
 *  - status none                -> 'invalid'   (no attempt exists for this subject+id)
 *  - anything else              -> 'failed'
 */
export function classifyVerifyResult(body) {
    if (!body || typeof body !== 'object') return 'failed';
    if (body.paid === true) return 'success';
    const status = String(body.status || '').trim().toLowerCase();
    if (status === 'pending' || status === 'processing') return 'pending';
    if (status === 'unreconcilable') return 'checking';
    if (status === 'none') return 'invalid';
    return 'failed';
}

/** €89 for whole euros, €89.50 otherwise; '' for zero, negative or non-numeric. */
export function formatAmount(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return '';
    // n * 100 drifts (1.005 * 100 = 100.4999...); trim the drift before rounding.
    const cents = Math.round(Number((n * 100).toFixed(6)));
    return cents % 100 === 0 ? `€${cents / 100}` : `€${(cents / 100).toFixed(2)}`;
}

export function isCardMethod(method) {
    return String((method && method.source) || '').trim().toLowerCase() === 'card';
}

/** Split context.methods[] into bank tiles and the (single) card entry. */
// Bank tiles are shown for the customer's own market only. The LHV account lists
// the Latvian and Lithuanian branches of the same banks too (swed_ob_lv,
// seb_ob_lt, ...) with identical logos, and an Estonian customer who taps the
// Lithuanian Swedbank tile lands on the wrong bank login. Multi-region methods
// (Revolut) carry no country_code and are always shown.
export const HOME_BANK_COUNTRY = 'EE';

function isHomeMarketBank(method) {
    const country = String((method && method.country_code) || '').trim().toUpperCase();
    return country === '' || country === HOME_BANK_COUNTRY;
}

/**
 * Split context.methods[] into bank tiles and the (single) card entry. Banks are
 * limited to the home market; if that would leave none (an account without
 * Estonian banks), every bank is shown rather than an empty section.
 */
export function splitMethods(methods) {
    const list = Array.isArray(methods) ? methods.filter((m) => m && typeof m === 'object') : [];
    const card = list.find(isCardMethod) || null;
    const allBanks = list.filter((m) => !isCardMethod(m));
    const homeBanks = allBanks.filter(isHomeMarketBank);
    return { banks: homeBanks.length > 0 ? homeBanks : allBanks, card };
}

/** The https logo EveryPay sent for a method, or '' (the tile then renders text). */
export function methodLogoUrl(method) {
    const url = method && typeof method.logo_url === 'string' ? method.logo_url.trim() : '';
    return url.startsWith('https://') ? url : '';
}
